import type { CSSProperties } from 'react'
import styled from '@xstyled/styled-components'
import type { Orientation, SliderSize } from './types'
import { orient } from './utils'

function getSizablePartStyles(size: SliderSize) {
  switch (size) {
    case 'sm':
      return {
        thumb: { '--slider-thumb-size': 10 },
        track: {
          '--slider-track-border-radius': '0.0625rem',
          '--slider-track-size': 2
        }
      }
    case 'md':
      return {
        thumb: { '--slider-thumb-size': 14 },
        track: {
          '--slider-track-border-radius': '0.125rem',
          '--slider-track-size': 4
        }
      }
    case 'lg':
      return {
        thumb: { '--slider-thumb-size': 16 },
        track: {
          '--slider-track-border-radius': '0.1875rem',
          '--slider-track-size': 6
        }
      }
  }
}

function getThumbOrientationStyles(orientation: Orientation) {
  return orient({
    orientation: orientation,
    vertical: {
      left: '50%',
      transform: `translateX(-50%)`,
      '&:active, [data-active]': {
        transform: `translateX(-50%) scale(1.15)`
      }
    } as CSSProperties,
    horizontal: {
      top: '50%',
      transform: `translateY(-50%)`,
      '&:active, [data-active]': {
        transform: `translateY(-50%) scale(1.15)`
      }
    } as CSSProperties
  })
}

export const SliderRoot = styled.divBox<{ orientation: Orientation }>(
  props => ({
    display: 'inline-block',
    position: 'relative',
    cursor: 'pointer',
    '&:disabled, [data-disabled]': {
      opacity: 0.6,
      cursor: 'default',
      pointerEvents: 'none'
    },
    ...orient({
      orientation: props.orientation,
      horizontal: {
        width: '100%'
      },
      vertical: {
        height: '100%'
      }
    })
  })
)

export const SliderTrackRoot = styled.divBox<{
  size: SliderSize
  orientation: Orientation
}>(props => ({
  ...getSizablePartStyles(props.size).track,
  width: 'var(--slider-track-size)',
  height: 'var(--slider-track-size)',
  borderRadius: 'var(--slider-track-border-radius)',
  overflow: 'hidden',
  background: props.theme.colors.grey.l3,
  '&:disabled, [data-disabled]': {
    background: props.theme.colors.disabled.l1
  },
  ...orient({
    orientation: props.orientation,
    horizontal: { height: 'var(--slider-track-size)' },
    vertical: { width: 'var(--slider-track-size)' }
  })
}))

export const SliderTrackFilledRoot = styled.div(props => ({
  width: 'inherit',
  height: 'inherit',
  background: props.theme.colors.primary.base
}))

export const SliderThumbRoot = styled.divBox<{
  size: SliderSize
  orientation: Orientation
}>(props => ({
  ...getSizablePartStyles(props.size).thumb,
  width: 'var(--slider-thumb-size)',
  height: 'var(--slider-thumb-size)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute',
  outline: 'transparent solid 2px',
  outlineOffset: '2px',
  zIndex: 1,
  borderRadius: '100%',
  background: props.theme.colors.white,
  boxShadow: props.theme.shadows.xs,
  border: '1px solid transparent',
  transitionProperty: 'transform, box-shadow',
  transitionDuration: '150ms',
  '&:focus-visible, &[data-focus]': {
    boxShadow: props.theme.shadows['outline-primary']
  },
  '&:disabled,  &[data-disabled]': {
    background: props.theme.colors.disabled.l1
  },
  ...getThumbOrientationStyles(props.orientation)
}))
